import type { ReactNode } from "react";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { SiteNav } from "./site-nav";
import { SiteFooter } from "./site-footer";

const ECOSYSTEM_MAP = [
  { to: "/soluciones/acreditacion-contratistas/mine-pass", label: "Mine Pass", pillar: "/soluciones/acreditacion-contratistas" },
  { to: "/soluciones/acreditacion-contratistas/examine", label: "Examine", pillar: "/soluciones/acreditacion-contratistas" },
  { to: "/soluciones/tecnologia-aprendizaje/vigia", label: "VigIA", pillar: "/soluciones/tecnologia-aprendizaje" },
  { to: "/soluciones/tecnologia-aprendizaje/athena-lms", label: "Athena LMS", pillar: "/soluciones/tecnologia-aprendizaje" },
  { to: "/soluciones/tecnologia-aprendizaje/face-id", label: "FaceID", pillar: "/soluciones/tecnologia-aprendizaje" },
  { to: "/soluciones/tecnologia-aprendizaje/sonia", label: "SonIA", pillar: "/soluciones/tecnologia-aprendizaje" },
  { to: "/cursos/empresas", label: "Cursos Empresas", pillar: "/cursos/empresas" },
  { to: "/cursos/personas", label: "Cursos Personas", pillar: "/cursos/personas" },
];

export type SolucionTab = {
  label: string;
  to: string;
};

function ConoceTambien({ pathname }: { pathname: string }) {
  const items = ECOSYSTEM_MAP.filter((s) => !pathname.startsWith(s.pillar));
  if (items.length === 0) return null;
  return (
    <div className="mt-8 pt-6 border-t border-[var(--color-outline-variant)]">
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--color-on-surface-variant)] mb-3">
        Conoce también
      </p>
      <ul className="flex flex-col gap-2">
        {items.map(({ to, label }) => (
          <li key={to}>
            <Link
              to={to}
              className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-surface-container-low)] border border-[var(--color-outline-variant)] hover:bg-[var(--color-brand-coral)] hover:border-[var(--color-brand-coral)] transition-all duration-200"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-brand-coral)] group-hover:bg-white transition-colors" />
              <span className="text-sm font-semibold text-[var(--color-on-surface)] group-hover:text-white transition-colors">
                {label}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SolucionesPanel({
  eyebrow = "SOLUCIONES ECOSISTEMA MINE CLASS",
  title,
  intro,
  tabs,
  children,
}: {
  eyebrow?: string;
  title: string;
  intro?: string;
  tabs: SolucionTab[];
  children?: ReactNode;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const showEcosystemMap = pathname.startsWith("/soluciones");
  return (
    <div className="bg-white text-[var(--color-on-surface)] overflow-x-hidden min-h-screen flex flex-col">
      <SiteNav />

      <header className="bg-[var(--color-brand-purple)] text-white py-8 md:py-10">
        <div className="max-w-container-max mx-auto px-margin-desktop">
          <span className="inline-block py-1 px-3 bg-[var(--color-brand-coral)] mb-3 font-mono-data text-[10px] md:text-xs tracking-widest uppercase">
            {eyebrow}
          </span>
          <h1 className="text-3xl md:text-4xl font-bold leading-tight max-w-4xl">{title}</h1>
          {intro && <p className="text-base mt-3 text-white/80 max-w-3xl">{intro}</p>}
        </div>
      </header>

      <main className="flex-1 py-10 md:py-14 bg-white">
        <div className="max-w-container-max mx-auto px-margin-desktop">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
            {/* LEFT: chips (tabs) + conoce también (desktop sidebar) */}
            <aside className="lg:col-span-4 xl:col-span-3">
              <div className="lg:sticky lg:top-32">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--color-on-surface-variant)] mb-3">
                  Explora
                </p>
                <div
                  className="flex flex-wrap lg:flex-col gap-2"
                  role="tablist"
                  aria-label={title}
                >
                  {tabs.map((t) => (
                    <Link
                      key={t.to}
                      to={t.to}
                      role="tab"
                      className="px-4 py-2.5 rounded-full text-sm font-semibold border-2 transition-all duration-200 bg-[var(--color-brand-purple)] text-white border-[var(--color-brand-purple)] hover:bg-[var(--color-brand-coral)] hover:border-[var(--color-brand-coral)] hover:text-white lg:text-left"
                      activeProps={{
                        className:
                          "px-4 py-2.5 rounded-full text-sm font-semibold border-2 transition-all duration-200 bg-[var(--color-brand-coral)] text-white border-[var(--color-brand-coral)] shadow-md lg:text-left",
                      }}
                      activeOptions={{ exact: false }}
                    >
                      {t.label}
                    </Link>
                  ))}
                </div>

                {showEcosystemMap && (
                  <div className="hidden lg:block">
                    <ConoceTambien pathname={pathname} />
                  </div>
                )}
              </div>
            </aside>

            {/* RIGHT: dynamic content */}
            <section className="lg:col-span-8 xl:col-span-9 min-w-0">
              <div
                key={typeof window !== "undefined" ? window.location.pathname : title}
                className="animate-fade-in bg-[var(--color-surface-container-low)] border border-[var(--color-outline-variant)] rounded-2xl p-5 md:p-8"
              >
                {children ?? <Outlet />}
              </div>
            </section>

            {/* Conoce también: mobile, below the description/content */}
            {showEcosystemMap && (
              <div className="lg:hidden">
                <ConoceTambien pathname={pathname} />
              </div>
            )}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
